// Deciding whether the build being served is newer than the build running.
//
// Kept free of fetch, timers and the DOM so it can be tested as a table rather
// than through a browser — the same reason edge-function logic lives in
// `supabase/functions/_shared/`.
//
// The asymmetry that shapes every rule here: a *missed* update leaves someone on
// a stale build until the next check, which is the status quo. A *false* update
// reloads them into the identical build, which then prompts again, forever. So
// anything short of a confident "these two differ" answers false.

/** What the build id is in `npm run dev`, where no manifest is served. */
export const DEV_BUILD_ID = 'dev';

/**
 * Pull the build id out of a parsed `version.json`.
 *
 * Returns null rather than throwing for anything malformed: the caller reads
 * null as "learned nothing this tick" and stays quiet, whereas a throw inside a
 * polling loop surfaces as an unhandled rejection.
 */
export function readBuildId(manifest: unknown): string | null {
  if (typeof manifest !== 'object' || manifest === null || Array.isArray(manifest)) {
    return null;
  }
  const value = (manifest as { buildId?: unknown }).buildId;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Is the served build different from the one this tab is running?
 *
 * @param running  the id compiled into this bundle
 * @param fetched  the id just read from `version.json`, or null if unreadable
 */
export function isNewBuild(
  running: string | null | undefined,
  fetched: string | null | undefined
): boolean {
  if (!running || !fetched) return false;
  // A dev bundle has nothing meaningful to compare against.
  if (running === DEV_BUILD_ID) return false;
  return running !== fetched;
}
